/**
 * The data plane — the single place that decides HOW and WHEN data is fetched.
 *
 * Every read in the app goes through one of three tiers, and the tier decides
 * both the network priority and who is allowed to block:
 *
 *   critical     The data the destination page needs to render. Awaitable.
 *                Resolves from cache instantly when the entry is fresh; when
 *                stale it returns the stale data immediately and heals in the
 *                background (the UI never blocks on a refetch it already has
 *                content for). Only a genuinely cold cache streams.
 *
 *   intent       The user signalled they are going somewhere (pointerdown on a
 *                card, touchstart on a tab). Fired immediately — speculation is
 *                most valuable when it is a few hundred ms ahead of a certain
 *                navigation.
 *
 *   speculative  Nobody asked for it: viewport prefetch, sibling-tab priming.
 *                Scheduled while the main thread is idle and drained ONE AT A
 *                TIME through a single global queue, so background work can
 *                never compete with the page the user is actually looking at.
 *
 * Staleness is decided here and only here (see `isStale`), so a route loader
 * and a prefetcher can never disagree about whether a cache entry is usable.
 */
import type { QueryClient } from '@tanstack/react-query';
import { whenIdle } from '@/lib/prefetch/idle';

export type FetchPriority = 'critical' | 'intent' | 'speculative';

/** Query shape the plane understands; the factories in `@/lib/queries/detail`
 *  return exactly this, plain or infinite. */
export interface PlaneQuery {
  queryKey: readonly unknown[];
  queryFn: (context: { pageParam: unknown }) => Promise<unknown>;
  infinite?: boolean;
  initialPageParam?: unknown;
}

/** Freshness policy. Matches the app-wide staleTime; the plane is the only
 *  reader so loaders and prefetchers always agree. */
export const DEFAULT_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Intent prefetches repeat-throttle: finger wobble, a re-render, or a second
 * card for the same entity must not refire the fetch. Well below the stale
 * window, so a prefetch that lands still serves the real visit.
 */
const INTENT_TTL_MS = 60_000;

/**
 * Speculative rate window: a fast scroll through a long feed must not
 * stampede the API. This is a *window*, not a session cap — every entity that
 * comes into view still gets primed, just not more than N per window.
 *
 * 4, not 8: landing on discovery put eight detail reads on the wire while the
 * user was still looking at the first screen, and on a phone that competes with
 * the list request that actually gates the page. A tap already fires its own
 * intent prefetch on pointerdown, so the entities the user really reaches are
 * still primed before the navigation commits.
 */
const SPECULATIVE_WINDOW_MS = 5_000;
const SPECULATIVE_MAX_PER_WINDOW = 4;

const lastPrefetchAt = new Map<string, number>();
const speculativeFireTimes = new Map<string, number[]>();

const keyOf = (query: { queryKey: readonly unknown[] }): string => JSON.stringify(query.queryKey);

/** True when the cache entry is missing, failed, or older than `staleTime`. */
export function isStale(
  client: QueryClient,
  queryKey: readonly unknown[],
  staleTime = DEFAULT_STALE_TIME_MS
): boolean {
  const state = client.getQueryState(queryKey);
  if (!state || state.data === undefined) return true;
  if (state.status !== 'success') return true;
  return Date.now() - state.dataUpdatedAt >= staleTime;
}

/** True when the entry can be rendered as-is (present, succeeded, fresh). */
export function isFresh(
  client: QueryClient,
  queryKey: readonly unknown[],
  staleTime = DEFAULT_STALE_TIME_MS
): boolean {
  return !isStale(client, queryKey, staleTime);
}

function runQuery(client: QueryClient, query: PlaneQuery): Promise<unknown> {
  return query.infinite
    ? client.ensureInfiniteQueryData(query as never)
    : client.ensureQueryData(query as never);
}

/**
 * CRITICAL — a route loader's own data.
 *
 * Returns `cached` when the entry exists (and heals it in the background when
 * stale, so a slow refetch never blocks a page that already has content), or
 * `undefined` when the cache is cold and the caller must stream the promise
 * its `<Await>` boundary renders against.
 */
export function readCritical(
  client: QueryClient,
  query: PlaneQuery,
  opts?: { staleTime?: number }
): { cached: unknown; stream: () => Promise<unknown> } {
  // The reader is optional-chained because loader-contract tests stub the
  // client with ensureQueryData only; a client with no cache reader simply
  // reads as a cold cache.
  const cached = client.getQueryData?.(query.queryKey);
  // Delegate to the caller's own queryFn instead of wrapping it in a plane
  // ensure* for the same key: a loader's refresh already owns its transport
  // (ensureQueryData / fetchQuery / ensureInfiniteQueryData), and a second
  // ensure* for the same key would await the very fetch it is resolving — a
  // cold loader would never settle. Infinite queries keep the plane's wrapper,
  // because their queryFn is the bare per-page fetch.
  const refresh = query.infinite
    ? () => runQuery(client, query)
    : () => query.queryFn({ pageParam: query.initialPageParam });
  if (cached !== undefined) {
    if (isStale(client, query.queryKey, opts?.staleTime)) {
      // Stale but renderable: hand the caller the data now and repair in the
      // background. NOTE: this must be refetchQueries, not the caller's
      // refresh closure — ensureQueryData() only revalidates when explicitly
      // asked (v5 requires `revalidateIfStale`), so calling it here returned
      // the cached value and healed nothing. refetchQueries reuses the
      // queryFn registered on the entry, which is also why it cannot
      // re-enter this function and deadlock.
      void client.refetchQueries({ queryKey: query.queryKey, exact: true }).catch(() => undefined);
    }
    return { cached, stream: refresh };
  }
  return { cached: undefined, stream: refresh };
}

function speculativeBudgetAvailable(kind: string, windowMs: number, max: number): boolean {
  const now = Date.now();
  const recent = (speculativeFireTimes.get(kind) ?? []).filter((t) => now - t < windowMs);
  speculativeFireTimes.set(kind, recent);
  return recent.length < max;
}

function stampSpeculative(kind: string): void {
  const times = speculativeFireTimes.get(kind) ?? [];
  times.push(Date.now());
  speculativeFireTimes.set(kind, times);
}

const speculativeQueue: Array<() => Promise<unknown>> = [];
let draining = false;

function drainSpeculativeQueue(): void {
  if (draining) return;
  draining = true;
  whenIdle(() => {
    void (async () => {
      try {
        while (speculativeQueue.length > 0) {
          const task = speculativeQueue.shift();
          if (!task) break;
          try {
            await task();
          } catch {
            // speculation is best-effort
          }
        }
      } finally {
        draining = false;
      }
    })();
  });
}

function fire(
  client: QueryClient,
  query: PlaneQuery,
  priority: FetchPriority,
  ttlMs: number
): Promise<void> {
  const key = keyOf(query);
  const now = Date.now();
  const last = lastPrefetchAt.get(key);
  if (last !== undefined && now - last < ttlMs) return Promise.resolve();
  lastPrefetchAt.set(key, now);
  if (priority === 'speculative') {
    // Stamped now, so a burst of observers for the same key dedupes even
    // though the actual fetch is deferred.
    return Promise.resolve();
  }
  return runQuery(client, query)
    .then(() => undefined)
    .catch(() => undefined);
}

/**
 * PREFETCH — the one entry point for everything speculative or intent-driven.
 *
 * `priority: 'intent'` fires now. `priority: 'speculative'` is rate-limited
 * per kind, deduped per key, and drained one-at-a-time while idle.
 */
export function prefetch(
  client: QueryClient,
  query: PlaneQuery,
  priority: FetchPriority,
  opts?: {
    kind?: string;
    ttlMs?: number;
    windowMs?: number;
    maxPerWindow?: number;
    defer?: boolean;
  }
): Promise<void> {
  const ttlMs = opts?.ttlMs ?? INTENT_TTL_MS;
  if (priority !== 'speculative') return fire(client, query, priority, ttlMs);

  const kind = opts?.kind ?? 'default';
  const windowMs = opts?.windowMs ?? SPECULATIVE_WINDOW_MS;
  const maxPerWindow = opts?.maxPerWindow ?? SPECULATIVE_MAX_PER_WINDOW;
  const key = keyOf(query);
  const now = Date.now();
  const last = lastPrefetchAt.get(key);
  if (last !== undefined && now - last < ttlMs) return Promise.resolve();
  if (!speculativeBudgetAvailable(kind, windowMs, maxPerWindow)) return Promise.resolve();
  lastPrefetchAt.set(key, now);
  stampSpeculative(kind);
  if (!opts?.defer) {
    // Rate-limited but immediate: the viewport case, where the fetch should
    // overlap the scroll rather than wait for an idle window.
    return runQuery(client, query)
      .then(() => undefined)
      .catch(() => undefined);
  }
  speculativeQueue.push(() => runQuery(client, query));
  drainSpeculativeQueue();
  return Promise.resolve();
}
